import React, { useMemo } from "react";
import Select, { components } from "react-select";
import { useTranslation } from "react-i18next";

import AvatarsCollection from "../AvatarsCollection";
import User from "../User";

import { mapMemberToOption, mapRoleName } from "./Selector.mappers";
import * as Styled from "./Selector.styled";
import { IconArrow, IconPeople, IconPerson } from "../Icons";

const Input = ({ selected, menuIsOpen, isMulti }) => {
  const { t } = useTranslation();

  const icon = isMulti ? <IconPeople /> : <IconPerson />;
  const name = menuIsOpen ? "" : selected?.label ?? t("Select team members");
  const mappedRoleName = menuIsOpen ? "" : mapRoleName(selected?.roleName);

  return (
    <>
      <User name={name} image={selected?.picture} icon={icon} roleName={mappedRoleName} />
      {selected?.length ? <AvatarsCollection avatars={selected} /> : null}
    </>
  );
};

const ValueContainer = ({ children, ...props }) => {
  const { isMulti, selectProps } = props;
  const { value: selected, menuIsOpen } = selectProps;

  return (
    <components.ValueContainer {...props}>
      <Input selected={selected} menuIsOpen={menuIsOpen} isMulti={isMulti} />
      {children}
      {!isMulti && <IconArrow />}
    </components.ValueContainer>
  );
};

const Option = (props) => {
  const { label: name, picture, roleName } = props.data;
  const mappedRoleName = mapRoleName(roleName);

  return (
    <components.Option {...props}>
      <User name={name} image={picture} roleName={mappedRoleName} />
    </components.Option>
  );
};

const AddNewMemberOption = (props) => {
  return (
    <Styled.AddMemberOption {...props}>
      <div onClick={() => props.onAddNewOption()}>+ Neues Teammitglied einladen</div>
    </Styled.AddMemberOption>
  );
};

const DefaultNoOptions = (props) => {
  return <components.NoOptionsMessage {...props} />;
};

const Selector = ({
  title,
  description,
  members,
  role,
  isMulti,
  onChange,
  onAddNewOption,
  canAddNewMember = false
}) => {
  const options = members.map((member) => mapMemberToOption(member));
  const values = members
    .filter((member) => member.roles?.includes(role))
    .map((member) => mapMemberToOption(member));

  const customComponents = useMemo(
    () => ({
      ClearIndicator: () => null,
      DropdownIndicator: () => null,
      IndicatorsContainer: () => null,
      IndicatorSeparator: () => null,
      Option,
      Placeholder: () => null,
      SingleValue: () => null,
      ValueContainer,
      NoOptionsMessage: canAddNewMember
        ? (props) => <AddNewMemberOption {...props} onAddNewOption={onAddNewOption} />
        : DefaultNoOptions
    }),
    [canAddNewMember]
  );

  return (
    <Styled.Selector>
      <Styled.Label margin={!description}>{title}</Styled.Label>
      {description && <Styled.Description>{description}</Styled.Description>}

      <Select
        closeMenuOnSelect={!isMulti}
        components={customComponents}
        defaultValue={isMulti ? values : values[0]}
        hideSelectedOptions={false}
        isMulti={isMulti}
        onChange={(selection) => onChange(selection)}
        openMenuOnClick
        options={options}
        styles={Styled.customStyles}
      />
    </Styled.Selector>
  );
};

export default Selector;
